import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./JoinedLobby.css";
import { useScribbleLobby } from "../../hooks/useScribbleLobby";
import { avatarSrcForIndex } from "../../lib/lobbyAvatars";
import { startTabooLobbyGame } from "../../lib/tabooGame";

export default function JoinedLobby() {
  const navigate = useNavigate();
  const seenIdsRef = useRef(new Set());
  const [newIds, setNewIds] = React.useState(() => new Set());
  const [starting, setStarting] = useState(false);

  const {
    lobbyName,
    players,
    phase,
    isHost,
    joining,
    error,
    configured,
  } = useScribbleLobby({ autoJoin: true });

  useEffect(() => {
    const nextNew = new Set();
    players.forEach((p) => {
      if (!seenIdsRef.current.has(p.id)) {
        seenIdsRef.current.add(p.id);
        nextNew.add(p.id);
      }
    });
    if (nextNew.size > 0) {
      setNewIds((prev) => new Set([...prev, ...nextNew]));
      const t = window.setTimeout(() => {
        setNewIds((prev) => {
          const copy = new Set(prev);
          nextNew.forEach((id) => copy.delete(id));
          return copy;
        });
      }, 700);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [players]);

  useEffect(() => {
    if (phase === "select_player" || phase === "playing") {
      navigate("/game", { replace: true, state: { lobby_name: lobbyName } });
    }
  }, [phase, isHost, navigate, lobbyName]);

  const statusText = useMemo(() => {
    if (!configured) return "Connecting…";
    if (joining) return "Joining lobby…";
    if (error) return error;
    if (phase === "select_player") return "Host is setting up the round…";
    if (phase === "playing") return "Round in progress…";
    if (phase === "lobby" && isHost) return "";
    return "Game will start soon…";
  }, [configured, joining, error, phase, isHost]);

  const handleStartGame = async () => {
    if (!lobbyName || !isHost || starting) return;
    setStarting(true);
    try {
      let roundCount = 3;
      let timerMin = 1;
      try {
        const gs = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
        roundCount = Number(gs.rounds ?? gs.questions) || 3;
        timerMin = Number(gs.time ?? gs.time_duration) || 1;
      } catch {
        /* ignore */
      }
      const ids = players.map((p) => p.id);
      await startTabooLobbyGame(lobbyName, {
        roundCount,
        playerIds: ids,
        timerSeconds: Math.max(60, timerMin * 60),
      });
      navigate("/select-next-player", {
        replace: true,
        state: { lobby_name: lobbyName },
      });
    } catch (e) {
      console.error(e);
    } finally {
      setStarting(false);
    }
  };

  return (
    <main className="joined-lobby-page">
      <header className="joined-lobby-header">
        <h1 className="joined-lobby-title">LOBBY</h1>
        <p className="joined-lobby-player-count">
          {players.length} {players.length === 1 ? "player" : "players"} joined
        </p>
      </header>

      <div className="joined-lobby-grid" aria-live="polite">
        {players.map((player, index) => (
          <div
            key={player.id}
            className={`joined-lobby-avatar${newIds.has(player.id) ? " joined-lobby-avatar--pop" : ""}`}
            style={{ animationDelay: `${(index % 10) * 0.2}s` }}
            title={player.name}
          >
            <img src={avatarSrcForIndex(player.avatarIndex)} alt="" />
          </div>
        ))}
      </div>

      <div className="joined-lobby-status-wrap">
        {statusText ? <div className="joined-lobby-status-pill">{statusText}</div> : null}
        <p className="joined-lobby-subtext">
          {isHost ? "You are the host" : "Please wait for the host"}
        </p>
        {isHost && phase === "lobby" && (
          <button
            type="button"
            className="joined-lobby-host-btn"
            onClick={handleStartGame}
            disabled={starting || players.length === 0}
          >
            {starting ? "Starting…" : "Start Game"}
          </button>
        )}
      </div>
    </main>
  );
}
